import React, { useState } from 'react';
import { FiMail, FiX } from 'react-icons/fi';
import styled from 'styled-components';
import api from '../../../../../services/api';
import { useModal } from '../../../../../hooks/Modal';
import { useToast } from '../../../../../hooks/Toast';
import { useAuth } from '../../../../../hooks/Auth';

// =========================================================
// Templates disponíveis (espelho do INVOICE_EMAIL_TEMPLATES do backend)
// =========================================================
const TEMPLATES = [
  {
    key: 'reminder_before_due',
    endpoint: 'reminder-before-due',
    label: 'Lembrete pré-vencimento',
    desc: 'Lembrete ao aluno enviado antes do vencimento.',
    audience: 'student' as const,
    needsSequence: false
  },
  {
    key: 'reminder_after_due',
    endpoint: 'reminder-after-due',
    label: 'Lembrete pós-vencimento',
    desc: 'Lembrete ao aluno após vencimento (1º ou 2º envio).',
    audience: 'student' as const,
    needsSequence: true
  },
  {
    key: 'collection_letter',
    endpoint: 'collection-letter',
    label: 'Carta de cobrança',
    desc: 'Notificação formal (60+ dias). Marca a fatura e envia cópia à equipe.',
    audience: 'student' as const,
    needsSequence: false
  },
  {
    key: 'credit_card_reminder',
    endpoint: 'card-reminder',
    label: 'Lembrete cartão recorrente',
    desc: 'Lembrete antecipado da cobrança recorrente no cartão.',
    audience: 'student' as const,
    needsSequence: false
  },
  {
    key: 'team_default_notice',
    endpoint: 'team-default-notice',
    label: 'Notificação à equipe — inadimplência',
    desc: 'Avisa especializacoes@fundmed.org.br da inadimplência.',
    audience: 'team' as const,
    needsSequence: false
  },
  {
    key: 'team_regularization',
    endpoint: 'team-regularization',
    label: 'Notificação à equipe — regularização',
    desc: 'Avisa a equipe que o débito foi regularizado.',
    audience: 'team' as const,
    needsSequence: false
  }
];

// =========================================================
// Styled
// =========================================================
const Container = styled.div`
  background: #fff;
  border-radius: 10px;
  min-width: 380px;
  max-width: 540px;
  width: 100%;
  color: #1f2937;
  font-family: 'Inter', -apple-system, sans-serif;
`;
const Header = styled.div`
  padding: 18px 22px;
  border-bottom: 1px solid #f3f4f6;
  display: flex; align-items: flex-start; gap: 12px;
  h3 { font-size: 16px; font-weight: 800; color: #111827; }
  p { font-size: 12px; color: #6b7280; margin-top: 2px; }
  > div:first-child { flex: 1; }
  > button {
    background: none; border: none; cursor: pointer; color: #9ca3af;
    &:hover { color: #111827; }
  }
`;
const InvoiceChip = styled.div`
  background: rgba(111,33,118,.06);
  border-radius: 8px;
  padding: 10px 12px;
  margin: 14px 22px 0;
  font-size: 12px;
  color: #6b7280;
  strong { color: #1f2937; }
`;
const Body = styled.div`
  padding: 14px 22px 6px;
  max-height: 60vh;
  overflow-y: auto;
`;
const Card = styled.button<{ $audience: 'student'|'team' }>`
  display: block; width: 100%; text-align: left;
  padding: 12px 14px; border: 1.5px solid #e5e7eb; border-radius: 8px;
  background: #fff; cursor: pointer; transition: all .12s; margin-bottom: 8px;
  &:hover:not(:disabled) { border-color: var(--adminColor); background: rgba(111,33,118,.04); }
  &:disabled { opacity: .55; cursor: not-allowed; }
  .title-row { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
  .title { font-size: 13px; font-weight: 700; color: #111827; }
  .audience {
    font-size: 10px; font-weight: 600; padding: 2px 6px; border-radius: 999px;
    background: ${p => p.$audience === 'team' ? '#fef3c7' : 'rgba(111,33,118,.1)'};
    color:      ${p => p.$audience === 'team' ? '#92400e' : 'var(--adminColor)'};
  }
  .desc { font-size: 12px; color: #6b7280; line-height: 1.4; }
`;
const SeqRow = styled.div`
  display: flex; gap: 4px; padding: 4px;
  background: #f3f4f6; border-radius: 8px;
  margin-top: 8px; width: fit-content;
  button {
    border: none; background: transparent; padding: 5px 10px; border-radius: 6px;
    font-size: 11px; font-weight: 600; color: #6b7280; cursor: pointer;
    &.active { background: #fff; color: var(--adminColor); box-shadow: 0 1px 3px rgba(0,0,0,.08); }
  }
`;
const Footer = styled.div`
  padding: 12px 22px; border-top: 1px solid #f3f4f6;
  display: flex; gap: 10px; justify-content: flex-end;
`;
const BtnCancel = styled.button`
  height: 36px; padding: 0 14px; border-radius: 8px;
  border: 1.5px solid #e5e7eb; background: #fff; color: #374151;
  font-size: 12px; font-weight: 600; cursor: pointer;
  &:hover { background: #f9fafb; }
`;

// =========================================================
// Modal
// =========================================================
const SendModal: React.FC<{ lineData: any; onClose: () => void }> = ({ lineData, onClose }) => {
  const { addToast } = useToast();
  const { handleApiErrors } = useAuth();
  const [sending, setSending] = useState<string | null>(null);
  const [sequence, setSequence] = useState<1 | 2>(1);

  const handleSend = async (tpl: typeof TEMPLATES[number]) => {
    setSending(tpl.key);
    try {
      const body = tpl.needsSequence ? { sequence } : {};
      await api.post(`/invoice-mail/${tpl.endpoint}/${lineData._id}`, body);
      addToast({ title: `${tpl.label} agendado`, type: 'success' });
      onClose();
    } catch (err) {
      addToast({ title: 'Erro ao agendar', type: 'error' });
      handleApiErrors(err);
    } finally {
      setSending(null);
    }
  };

  const studentName = lineData?.client_id_response?.name || '—';
  const installment = lineData?.installment_number || '—';
  const status      = lineData?.payment_status_id || '—';

  return (
    <Container>
      <Header>
        <div>
          <h3>Enviar e-mail da fatura</h3>
          <p>Escolha o template para envio imediato.</p>
        </div>
        <button onClick={onClose}><FiX size={16} /></button>
      </Header>
      <InvoiceChip>
        <strong>{studentName}</strong> · Fatura #{installment} · {status}
      </InvoiceChip>
      <Body>
        {TEMPLATES.map(tpl => (
          <Card
            key={tpl.key}
            $audience={tpl.audience}
            disabled={sending !== null}
            onClick={() => handleSend(tpl)}
          >
            <div className="title-row">
              <span className="title">{tpl.label}</span>
              <span className="audience">{tpl.audience === 'team' ? 'Equipe' : 'Aluno'}</span>
            </div>
            <div className="desc">{tpl.desc}</div>
            {tpl.needsSequence && (
              <SeqRow onClick={e => e.stopPropagation()}>
                <button className={sequence === 1 ? 'active' : ''} onClick={() => setSequence(1)}>1º envio</button>
                <button className={sequence === 2 ? 'active' : ''} onClick={() => setSequence(2)}>2º envio</button>
              </SeqRow>
            )}
          </Card>
        ))}
      </Body>
      <Footer>
        <BtnCancel onClick={onClose}>Fechar</BtnCancel>
      </Footer>
    </Container>
  );
};

// =========================================================
// Botão (vai em bodyModules — recebe lineData)
// =========================================================
interface Props { lineData: Record<string, any>; }

export default function SendInvoiceMail({ lineData }: Props): JSX.Element {
  const { addModal, removeModal } = useModal();

  const open = () => {
    addModal({
      key: 'send-invoice-mail',
      theme: 'whiteModal',
      title: '',
      content: <SendModal lineData={lineData} onClose={() => removeModal('send-invoice-mail')} />
    });
  };

  return (
    <button className="lineIcon" title="Enviar e-mail desta fatura" type="button" onClick={open}>
      <FiMail />
    </button>
  );
}
